import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { EMPTY_IDEA_FILTERS, IDEA_LIST_PAGE_SIZE } from './constants'
import { areFiltersEqual, getVisibleIdeas, hasActiveFilters, mergeUniqueById } from './utils'

const LOAD_ERROR = 'Could not load ideas. Please try again.'
const LOAD_MORE_ERROR = 'Could not load more ideas. Please try again.'

/**
 * Data for the Innovate All Ideas list (ideaList endpoint).
 *
 * - Status is an API filter: changing it reloads page 1 exactly once; applying the same
 *   statuses again does not call the API.
 * - Search is client-side over the loaded ideas (the endpoint has no search parameter).
 * - Only the latest request may update state, so a slow response for old filters can never
 *   overwrite the result of newer ones.
 * - "Load more" exists only for admins (the endpoint pages admin results only).
 *
 * @param {object}   args
 * @param {boolean}  args.isAdmin
 * @param {Function} args.fetchIdeaList  (query, { onSuccess, onFailure }) => void
 */
export const useInnovateIdeaList = ({ isAdmin, fetchIdeaList }) => {
  const [filters, setFilters] = useState(EMPTY_IDEA_FILTERS)
  const [searchTerm, setSearchTerm] = useState('')
  const [ideas, setIdeas] = useState([])
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  // 'idle' | 'loading' (page 1) | 'loadingMore' | 'error'
  const [requestState, setRequestState] = useState('idle')
  const [error, setError] = useState('')

  const latestRequestId = useRef(0)
  const failedOffset = useRef(0)
  // Latest callback / flag without making them effect dependencies.
  const fetchRef = useRef(fetchIdeaList)
  fetchRef.current = fetchIdeaList
  const isAdminRef = useRef(isAdmin)
  isAdminRef.current = isAdmin

  const loadPage = useCallback((pageOffset, appliedFilters) => {
    latestRequestId.current += 1
    const requestId = latestRequestId.current
    const isStale = () => requestId !== latestRequestId.current

    setRequestState(pageOffset ? 'loadingMore' : 'loading')
    setError('')

    fetchRef.current(
      { statuses: [...appliedFilters.statuses], offset: pageOffset, limit: IDEA_LIST_PAGE_SIZE },
      {
        onSuccess: (response) => {
          if (isStale()) return
          const page = Array.isArray(response?.data) ? response.data : []
          setIdeas((prev) => mergeUniqueById(pageOffset ? prev : [], page))
          setOffset(pageOffset)
          // Admin results are paged; an empty page means the end was reached.
          setHasMore(Boolean(isAdminRef.current) && page.length > 0)
          setRequestState('idle')
        },
        onFailure: () => {
          if (isStale()) return
          failedOffset.current = pageOffset
          if (!pageOffset) {
            // Don't keep showing results for statuses that are no longer applied.
            setIdeas([])
            setHasMore(false)
          }
          setError(pageOffset ? LOAD_MORE_ERROR : LOAD_ERROR)
          setRequestState('error')
        },
      },
    )
  }, [])

  // Ignore responses that arrive after unmount.
  useEffect(() => () => { latestRequestId.current += 1 }, [])

  // Single place that loads page 1: on mount and whenever the applied statuses change.
  useEffect(() => {
    loadPage(0, filters)
  }, [filters, loadPage])

  /** Applies API filters. Returns false (and does nothing) when unchanged. */
  const applyFilters = useCallback((next) => {
    const normalized = { statuses: [...(next?.statuses || [])] }
    if (areFiltersEqual(normalized, filters)) return false
    setFilters(normalized)
    return true
  }, [filters])

  /** Clears search and statuses; reloads only if statuses were applied. */
  const reset = useCallback(() => {
    setSearchTerm('')
    if (hasActiveFilters(filters)) setFilters(EMPTY_IDEA_FILTERS)
  }, [filters])

  const isBusy = requestState === 'loading' || requestState === 'loadingMore'

  const loadMore = useCallback(() => {
    if (!hasMore || isBusy) return
    loadPage(offset + IDEA_LIST_PAGE_SIZE, filters)
  }, [hasMore, isBusy, loadPage, offset, filters])

  const retry = useCallback(() => {
    if (requestState === 'error') loadPage(failedOffset.current, filters)
  }, [requestState, loadPage, filters])

  const visibleIdeas = useMemo(() => getVisibleIdeas(ideas, searchTerm), [ideas, searchTerm])

  return {
    ideas: visibleIdeas,
    filters,
    applyFilters,
    searchTerm,
    setSearchTerm,
    reset,
    loadMore,
    retry,
    hasMore,
    isLoading: requestState === 'loading',
    isLoadingMore: requestState === 'loadingMore',
    error,
    isFiltered: hasActiveFilters(filters) || Boolean(searchTerm.trim()),
  }
}